import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { FieldGroup } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { SlackIcon } from '@/Domain/components/icons/Slack.icon';

const SLACK_INTEGRATION_NAME = 'slack' as const;
const SLACK_QUERY_KEY = [QueryKey.integration.get, SLACK_INTEGRATION_NAME] as const;

const SLACK_APPS_URL = 'https://api.slack.com/apps';
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

const slackLegacySchema = z.object({
  slack_app_id: z.string(),
  slack_signing_secret: z.string(),
  slack_bot_user_token: z.string(),
  slack_reply_thread: z.boolean(),
  slack_reply_channel: z.boolean(),
});

type SlackLegacyForm = z.infer<typeof slackLegacySchema>;

interface SlackIntegrationParams {
  slack_bot_active?: boolean;
  slack_app_id?: string | null;
  slack_signing_secret?: string | null;
  slack_bot_user_token?: string | null;
  slack_reply_thread?: boolean;
  slack_reply_channel?: boolean;
}

interface SlackIntegrationResponse {
  name: 'slack';
  params: SlackIntegrationParams;
}

const DEFAULTS: SlackLegacyForm = {
  slack_app_id: '',
  slack_signing_secret: '',
  slack_bot_user_token: '',
  slack_reply_thread: false,
  slack_reply_channel: false,
};

const normalize = (params: SlackIntegrationParams | undefined): SlackLegacyForm => ({
  slack_app_id: params?.slack_app_id ?? DEFAULTS.slack_app_id,
  slack_signing_secret: params?.slack_signing_secret ?? DEFAULTS.slack_signing_secret,
  slack_bot_user_token: params?.slack_bot_user_token ?? DEFAULTS.slack_bot_user_token,
  slack_reply_thread: params?.slack_reply_thread ?? DEFAULTS.slack_reply_thread,
  slack_reply_channel: params?.slack_reply_channel ?? DEFAULTS.slack_reply_channel,
});

interface SlackLegacySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SlackLegacySheet = ({ open, onOpenChange }: SlackLegacySheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const { data, isLoading } = useQuery<SlackIntegrationResponse>({
    queryKey: SLACK_QUERY_KEY,
    enabled: open,
  });

  const params = data?.params;

  // Two stages: Connected = credentials saved (the bot token is the auth
  // indicator); Enabled = the bot is currently on (the active toggle).
  const isConnected = Boolean(params?.slack_bot_user_token);
  const isEnabled = Boolean(params?.slack_bot_active);

  const initialValues = useMemo(() => normalize(params), [params]);

  const { control, watch } = useForm<SlackLegacyForm>({
    resolver: zodResolver(slackLegacySchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  const mutation = useMutation<
    SlackIntegrationResponse,
    Error,
    { name: typeof SLACK_INTEGRATION_NAME; params: Partial<SlackIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData(SLACK_QUERY_KEY, (prev?: SlackIntegrationResponse) => ({
        name: SLACK_INTEGRATION_NAME,
        params: { ...(prev?.params ?? {}), ...response.params },
      }));
      toast.success(t('integration:slackLegacy.saved'));
    },
    onError: () => {
      toast.error(t('integration:slackLegacy.save_failed'));
    },
  });

  // Instant enable/disable toggle, decoupled from the settings Save: standardized
  // "<name> integration enabled/disabled" toast and never closes the sheet.
  const toggleMutation = useMutation<
    SlackIntegrationResponse,
    Error,
    { name: typeof SLACK_INTEGRATION_NAME; params: { slack_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData(SLACK_QUERY_KEY, (prev?: SlackIntegrationResponse) => ({
        name: SLACK_INTEGRATION_NAME,
        params: { ...(prev?.params ?? {}), ...response.params },
      }));
      const name = t('integration:slackLegacy.title');
      toast.success(
        variables.params.slack_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: () => {
      toast.error(t('integration:slackLegacy.save_failed'));
    },
  });

  const handleToggleActive = (checked: boolean) => {
    toggleMutation.mutate({
      name: SLACK_INTEGRATION_NAME,
      params: { slack_bot_active: checked },
    });
  };

  const appId = watch('slack_app_id');
  const signingSecret = watch('slack_signing_secret');
  const botUserToken = watch('slack_bot_user_token');
  const credentialsFilled =
    appId.trim() !== '' && signingSecret.trim() !== '' && botUserToken.trim() !== '';

  // Connect = authenticate by saving the (trimmed) credentials; like the OAuth
  // sheet, connecting also turns the bot on.
  const handleConnect = () => {
    if (!credentialsFilled) return;
    mutation.mutate(
      {
        name: SLACK_INTEGRATION_NAME,
        params: {
          slack_app_id: appId.trim(),
          slack_signing_secret: signingSecret.trim(),
          slack_bot_user_token: botUserToken.trim(),
          slack_bot_active: true,
        },
      },
      { onSuccess: () => setConnectDialogOpen(false) },
    );
  };

  // Disconnect = clear the credentials (de-authenticate); also turns the bot off.
  const handleDisconnect = () => {
    mutation.mutate({
      name: SLACK_INTEGRATION_NAME,
      params: {
        slack_app_id: '',
        slack_signing_secret: '',
        slack_bot_user_token: '',
        slack_bot_active: false,
      },
    });
  };

  const isBusy = isLoading || mutation.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        data-testid="slack-legacy-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <SlackIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="slack-legacy-title">
                  {t('integration:slackLegacy.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={isEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="slack-legacy-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integration:slackLegacy.overview')}
            prerequisites={(
              t('integration:slackLegacy.prerequisites', { returnObjects: true }) as string[]
            ).map((_, index) => (
              <Trans
                key={index}
                t={t}
                i18nKey={`integration:slackLegacy.prerequisites.${index}`}
                components={[
                  <a
                    key="slackapps"
                    href={SLACK_APPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                ]}
              />
            ))}
            docsHref="https://docs.quickchat.ai/channels/slack"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="slack-legacy-connect-enable-section"
          >
            {isConnected ? (
              <IntegrationConnectionCard
                data-testid="slack-legacy-connection-card"
                details={[
                  {
                    label: t('integration:slackLegacy.app_id.label'),
                    value: params?.slack_app_id ?? '',
                    'data-testid': 'slack-legacy-app-id',
                  },
                ]}
                actions={
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setConnectDialogOpen(true)}
                      data-testid="slack-legacy-reconnect-button"
                    >
                      <RefreshCw className="size-4" />
                      {t('integrations:reconnect')}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={handleDisconnect}
                      disabled={isBusy}
                      data-testid="slack-legacy-disconnect-button"
                    >
                      <Unplug className="size-4" />
                      {t('integrations:disconnect')}
                    </Button>
                  </>
                }
              />
            ) : (
              <Button
                type="button"
                variant="dark"
                size="sm"
                className="w-full"
                onClick={() => setConnectDialogOpen(true)}
                data-testid="slack-legacy-connect-button"
              >
                <Plug className="size-4" />
                {t('integrations:connect', { name: 'Slack' })}
              </Button>
            )}
          </IntegrationSection>

          <IntegrationSection
            title={t('integrations:section.settings')}
            data-testid="slack-legacy-settings-section"
          >
            <div>
              <FieldGroup className="gap-4">
                <IntegrationToggleField
                  id="slack_bot_active"
                  label={t('integrations:enable', { name: 'Slack' })}
                  checked={isEnabled}
                  onCheckedChange={handleToggleActive}
                  disabled={!isConnected || toggleMutation.isPending}
                  data-testid="slack_bot_active-switch"
                />

                <Controller
                  name="slack_reply_thread"
                  control={control}
                  render={({ field }) => (
                    <IntegrationToggleField
                      id="slack_reply_thread"
                      label={t('integration:slackLegacy.reply_thread.label')}
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value);
                        mutation.mutate({
                          name: SLACK_INTEGRATION_NAME,
                          params: { slack_reply_thread: value },
                        });
                      }}
                      disabled={!isConnected}
                      data-testid="slack_reply_thread-switch"
                    />
                  )}
                />

                <Controller
                  name="slack_reply_channel"
                  control={control}
                  render={({ field }) => (
                    <IntegrationToggleField
                      id="slack_reply_channel"
                      label={t('integration:slackLegacy.reply_channel.label')}
                      checked={field.value}
                      onCheckedChange={(value) => {
                        field.onChange(value);
                        mutation.mutate({
                          name: SLACK_INTEGRATION_NAME,
                          params: { slack_reply_channel: value },
                        });
                      }}
                      disabled={!isConnected}
                      data-testid="slack_reply_channel-switch"
                    />
                  )}
                />
              </FieldGroup>
            </div>
          </IntegrationSection>
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'Slack' })}
          description={t('integration:slackLegacy.connect_description')}
          submitLabel={t('integrations:connect', { name: 'Slack' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={handleConnect}
          submitDisabled={!credentialsFilled || isBusy}
          isSubmitting={mutation.isPending}
          data-testid="slack-legacy-connect-dialog"
        >
          <Controller
            name="slack_app_id"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="slack_app_id">{t('integration:slackLegacy.app_id.label')}</Label>
                <Input
                  id="slack_app_id"
                  data-testid="slack_app_id-input"
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.app_id.placeholder')}
                  className="w-full"
                />
              </div>
            )}
          />
          <Controller
            name="slack_signing_secret"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="slack_signing_secret">
                  {t('integration:slackLegacy.signing_secret.label')}
                </Label>
                <Input
                  id="slack_signing_secret"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-testid="slack_signing_secret-input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.signing_secret.placeholder')}
                  className="w-full"
                />
              </div>
            )}
          />
          <Controller
            name="slack_bot_user_token"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="slack_bot_user_token">
                  {t('integration:slackLegacy.bot_user_token.label')}
                </Label>
                <Input
                  id="slack_bot_user_token"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-testid="slack_bot_user_token-input"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('integration:slackLegacy.bot_user_token.placeholder')}
                  className="w-full"
                />
              </div>
            )}
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
